/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect } from 'react';

// Mantine
import { Pagination } from '@components/atoms/Pagination';
import {
	Box,
	Group,
	Image,
	Skeleton,
} from '@mantine/core';

// Components
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { CardProject } from '@components/molecules/CardProject';
import { MenuFilter } from '@components/molecules/MenuFilter';
import { SelectionCart } from '@components/molecules/SelectionCart';

// Styles
import classes from './Step2.module.css';

// Store
import useStore from '@store/index';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Api
import { MenuSelectFilter } from '@components/molecules';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { useMesurabilityProjects } from '@features/offsets/api/get-mesurability-projects';
import { useOffsetProjectMesurability } from '@features/offsets/hooks/useOffsetProjectMesurability';
import { convertArrayToLabelValue } from '@utils/functions';
import { ACTIVATED_FILTER_OPTIONS } from '@utils/options';
import { useTranslation } from 'react-i18next';
import { useMeasurabilities } from 'shared/api/get-measurabilities';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Step2 = () => {
	/* ********************************** Hooks ********************************* */
	const {
		offsetCreationDatas,
		//setOffsetCreationActiveStep,
		//offsetCreationActiveStep,
		setOffsetMesurabilityProjectsPage,
		setOffsetMesurabilityProjectsPageSize,
		offsetMesurabilityProjectsPage,
		offsetMesurabilityProjectsPageSize,
		offsetMesurabilityProjectsQueryParams,
		offsetMesurabilityProjects,
		offsetMesurabilityProjectsSelected,
		setOffsetMesurabilityProjects,
		//setOffsetProjectMixinAco2,
		setOffsetStep2Error,
		setIsEnabledOffsetNestStep,
	} = useStore();

	// APIs
	const { data: projects } = useMeasurabilities();

	const {
		data: offsetsMesurabilityProjectsApi,
		isLoading,
		total: totalItems,
		totalPages,
		isRefetching,
		isFetched,
	} = useMesurabilityProjects(
		offsetMesurabilityProjectsPage,
		offsetMesurabilityProjectsPageSize,
		offsetMesurabilityProjectsQueryParams
	);

	const {
		handleProjectFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		isSelectedProjectMesurability,
		handleNextStep,
		handleOnChangeTco2,
		handleAddProject,
		handleRemoveSelectedProject,
		handleActiveFilter,
	} = useOffsetProjectMesurability();

	const { t } = useTranslation();
	useEffect(() => {
		if (isRefetching && offsetsMesurabilityProjectsApi) {
			const _cloneOffsetMesurabilityProjects = JSON.parse(
				JSON.stringify(
					offsetsMesurabilityProjectsApi,
					null,
					2
				)
			);
			const _cloneOffsetMesurabilityProjectsSelected =
				JSON.parse(
					JSON.stringify(
						offsetMesurabilityProjectsSelected,
						null,
						2
					)
				);

			const newOffsetMesurabilityProjects =
				_cloneOffsetMesurabilityProjects.map(
					(objet1: any) => {
						const objet2Correspondant =
							_cloneOffsetMesurabilityProjectsSelected.find(
								(objet2: any) => objet2.id === objet1.id
							);
						if (objet2Correspondant) {
							return {
								...objet1,
								quantitytco2eq:
									objet2Correspondant.quantitytco2eq,
							};
						} else {
							return objet1;
						}
					}
				);

			setOffsetMesurabilityProjects(
				newOffsetMesurabilityProjects
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefetching]);

	useEffect(() => {
		if (
			isFetched &&
			!isRefetching &&
			offsetsMesurabilityProjectsApi
		) {
			setOffsetMesurabilityProjects(
				offsetsMesurabilityProjectsApi
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFetched]);

	useEffect(() => {
		if (
			offsetMesurabilityProjectsSelected &&
			offsetMesurabilityProjectsSelected.length == 0
		) {
			//setOffsetStep2Error(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [offsetMesurabilityProjectsSelected]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.content}>
				<Group className={classes.row}>
					<Box className={classes.containerFilter}>
						<MenuSelectFilter
							isPersist
							value={
								offsetMesurabilityProjectsQueryParams._id
									? offsetMesurabilityProjectsQueryParams._id
									: []
							}
							label={t('global.status')}
							size="lg"
							options={ACTIVATED_FILTER_OPTIONS}
							placeholder={t(
								'filters.placeholders.selectStatus'
							)}
							onApply={handleActiveFilter}
							withoutSearch
						/>
					</Box>
					<Box className={classes.containerFilter}>
						<MenuFilter
							isPersist
							value={
								offsetMesurabilityProjectsQueryParams._id
									? offsetMesurabilityProjectsQueryParams._id
									: []
							}
							label={t('global.project')}
							size="lg"
							options={convertArrayToLabelValue(projects)}
							placeholderSearch={t(
								'filters.placeholderSearch.searchProject'
							)}
							placeholder={t(
								'filters.placeholders.selectProject'
							)}
							onApply={handleProjectFilter}
						/>
					</Box>
					<Box className={classes.containerFilter}>
						<MenuFilter
							value={
								offsetMesurabilityProjectsQueryParams[
									'location.countryCode'
								]
									? offsetMesurabilityProjectsQueryParams[
											'location.countryCode'
										]['$in']
									: []
							}
							isPersist
							onApply={handleCountryFilter}
							label={t('global.country')}
							size="lg"
							options={EUROPEAN_COUNTRIES}
							placeholderSearch={t(
								'filters.placeholderSearch.searchCountry'
							)}
							placeholder={t(
								'filters.placeholders.selectCountry'
							)}
						/>
					</Box>
					<Box className={classes.containerFilter}>
						<MenuFilter
							isPersist
							value={
								offsetMesurabilityProjectsQueryParams.technology
									? offsetMesurabilityProjectsQueryParams.technology
									: []
							}
							onApply={handleTechnologyFilter}
							label="Project type"
							size="lg"
							options={PROJECT_TECHNOLOGIES}
							placeholderSearch={t(
								'filters.placeholderSearch.searchTechnology'
							)}
							placeholder={t(
								'filters.placeholders.selectTechnology'
							)}
						/>
					</Box>
					<AddFiltersButton>
						{t('global.addFilters')}
					</AddFiltersButton>
				</Group>
				<Box className={classes.contentCards}>
					{isLoading &&
						Array.from(Array(5).keys()).map(() => (
							<Skeleton
								height={260}
								width={260}
								radius="md"
							/>
						))}
					{!isLoading &&
						Array.isArray(offsetMesurabilityProjects) &&
						offsetMesurabilityProjects.length == 0 && (
							<Box className={classes.containerNoData}>
								<Image
									src={NoRecordsIcon}
									className={classes.iconNoRecords}
								/>
								No datas
							</Box>
						)}

					{!isLoading &&
						Array.isArray(offsetMesurabilityProjects) &&
						offsetMesurabilityProjects.length > 0 &&
						offsetMesurabilityProjects.map(
							(project: any) => (
								<CardProject
									disableActions={false}
									disabled={project.isDisabled}
									disabledLabel={t(
										'offsets.projectLacksQte'
									)}
									key={project.id}
									size={'sm'}
									type={'measurability'}
									data={project}
									isAdded={isSelectedProjectMesurability(
										project.id,
										offsetMesurabilityProjectsSelected
									)}
									tco2={project.quantitytco2eq}
									onAdd={() => {
										handleAddProject(project);
									}}
									onChangeTco2={(tco2: number) => {
										handleOnChangeTco2(project, tco2);
									}}
								/>
							)
						)}
				</Box>
				{/* Pagination */}
				<Box className={classes.containerPagination}>
					<Pagination
						pagesize={offsetMesurabilityProjectsPageSize}
						totalitems={totalItems}
						activepage={offsetMesurabilityProjectsPage}
						handlechangesize={function (
							_size: string | number
						) {
							setOffsetMesurabilityProjectsPageSize(
								_size
							);
							setOffsetMesurabilityProjectsPage(1);
						}}
						onChange={(data) => {
							setOffsetMesurabilityProjectsPage(data);
						}}
						total={totalPages ? totalPages : 0}
					/>
				</Box>
			</Box>
			<Box className={classes.cartBar}>
				<SelectionCart
					handleConfirm={handleNextStep}
					handlechangequantity={(_item, _qte) => {
						handleOnChangeTco2(_item, _qte);
					}}
					max={offsetCreationDatas.quantity}
					items={offsetMesurabilityProjectsSelected}
					onRemove={handleRemoveSelectedProject}
					size={'sm'}
					title={t('global.projects')}
					type={'carbon'}
					handlechangeprice={() => {}}
					setError={(isError: boolean) => {
						setOffsetStep2Error(isError);
					}}
					onchangestatus={(_status: boolean) => {
						setIsEnabledOffsetNestStep(_status);
					}}
				/>
			</Box>
		</Box>
	);
};
